import { useEffect, useRef, useState } from 'react';
import { CoreAPI, useIdentity } from '../core';
import { useFramework } from '../instance';
import { getStageInstance } from './instance';
import { getStageState, useStageState } from './states/stage-state';
import { checkIsLongImageMode } from './utils';

export const useActiveStageIndex = () => {
    return useStageState()((state) => state.activeStageIndex);
};

export const useActiveOriginIndex = (from = 'from') => {
    const activeStageIndex = useActiveStageIndex();
    // console.log('useActiveOriginIndex', from, activeStageIndex);

    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const [_, refresh] = useState<number>(0);

    useEffect(() => {
        const handler = coreApi.events.onRerenderAll(() => {
            refresh((prev) => prev + 1);
        });
        return () => {
            coreApi.events.off(handler);
        };
    }, []);

    return coreApi.convertStageToOrigin(activeStageIndex);
};

export const useStageUnit = () => {
    return useStageState()((state) => state.stageUnit);
};

export const useStageType = () => {
    return useStageState()((state) => state.stageType);
};

export const useViewWidth = () => {
    return useStageState()((state) => state.viewWidth);
};

export const useViewHeight = () => {
    return useStageState()((state) => state.viewHeight);
};

export const useStageScrollPosition = () => {
    return useStageState()((state) => state.stageScrollPosition);
};

export const useSetStageScrollPosition = () => {
    return useStageState()((state) => state.setStageScrollPosition);
};

export const useScale = () => {
    return useStageState()((state) => state.scale);
};

export const useIsAutoScale = () => {
    return useStageState()((state) => state.isAutoScale);
};

export const useIsPickStage = () => {
    return useStageState()((state) => state.isPickStage);
};

export const useIsStageVisible = () => {
    return useStageState()((state) => state.isStageVisible);
};

export const useViewPortPadding = () => {
    return useStageState()((state) => state.viewPortPadding);
};

export const useIsHideLayers = () => {
    return useStageState()((state) => state.isHideLayers);
};

export const useIsLongImageMode = () => {
    const stageType = useStageType();
    return checkIsLongImageMode(stageType);
};

export const useStageList = () => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);

    const [renderStageList, setRenderStageList] = useState<number[]>(coreApi.getRenderStage());

    useEffect(() => {
        const handler = coreApi.events.onRerenderAll((renderStageList) => {
            setRenderStageList(renderStageList);
        });
        return () => {
            coreApi.events.off(handler);
        };
    }, []);

    return renderStageList;
};

export const useStageListLength = () => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);

    const [length, setLength] = useState<number>(coreApi.getRenderStageLength());

    useEffect(() => {
        const handler = coreApi.events.onRerenderAll(() => {
            setLength(coreApi.getRenderStageLength());
        });
        return () => {
            coreApi.events.off(handler);
        };
    }, []);

    return length;
};

export const useLayerTransform = (layerId: string) => {
    const refNode = useRef<HTMLDivElement>(null);
    const [_, refresh] = useState(0);
    const identity = useIdentity();

    useEffect(() => {
        const coreApi = CoreAPI(identity);
        // const translateHandle = coreApi.events.onTranslate((id) => {
        //     // if (id !== layerId) {
        //     //     const wrapLayer = coreApi.getWrapLayer(layerId, true);
        //     //     if (id !== wrapLayer?.id) return;
        //     // }
        //     // const node = refNode.current;
        //     // if (!node) return;
        //     // const layer = coreApi.getLayer(layerId);
        //     // const { left, top, rotate } = layer;
        //     // const scale = getStageState(identity).scale;
        //     // node.style.transform = `translate(${left * scale}px, ${top * scale}px) rotate(${rotate}deg)`;
        // });
        // const handle = coreApi.events.onActiveLayerDataUpdate((layer, pickLayer) => {
        //     if (layerId === layer?.id || layerId === pickLayer?.id) {
        //         refresh((v) => v + 1);
        //     } else {
        //         if (coreApi.getLayer(layerId)) {
        //             const wrapLayer = coreApi.getWrapLayer(layerId, true);
        //             if (wrapLayer && (wrapLayer.id === layer?.id || wrapLayer.id === pickLayer?.id)) {
        //                 refresh((v) => v + 1);
        //             }
        //         }
        //     }
        // });
        const layerChangeHandle = coreApi.events.onLayerChange(() => {
            // console.log('ot layerChangeHandle', layerId);
            const node = refNode.current;
            if (!node) return;
            const layer = coreApi.getLayer(layerId);
            const { left, top, rotate } = layer;
            const scale = getStageState(identity).scale;
            node.style.transform = `translate(${left * scale}px, ${top * scale}px) rotate(${rotate}deg)`;
            refresh((v) => v + 1);
        }, layerId);

        return () => {
            // coreApi.events.off(handle);
            // coreApi.events.off(translateHandle);
            coreApi.events.off(layerChangeHandle);
        };
    }, [layerId]);

    return refNode;
};

/**
 * 图层通过修改 Translate 的定位
 */
export const useLayerTranslate = (layerId?: string) => {
    const identity = useIdentity();
    const scale = useScale();
    const refNode = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!layerId) return;
        const coreApi = CoreAPI(identity);
        const handle = coreApi.events.onTranslate((id) => {
            if (id !== layerId) return;
            const node = refNode.current;
            if (!node) return;
            const layer = coreApi.getLayer(layerId);
            const { left, top, rotate } = layer;
            node.style.transform = `translate(${left * scale}px, ${top * scale}px) rotate(${rotate}deg)`;
        });
        return () => {
            coreApi.events.off(handle);
        };
    }, [scale, layerId]);

    return refNode;
};

export function useStagePadding() {
    const [paddingLeft, setPaddingLeft] = useState(0);
    const [paddingTop, setPaddingTop] = useState(0);

    const scale = useScale();
    const viewHeight = useViewHeight();
    const viewWidth = useViewWidth();
    const stageScrollPosition = useStageScrollPosition();
    const viewPortPadding = useViewPortPadding();
    const identity = useIdentity();
    const framework = useFramework();
    // 维护一个 stage change 的版本号, 一起加入绘制标尺的 effect 依赖里, 避免舞台宽高发生变化时重复绘制
    const [stageVersion, setStageVersion] = useState(0);

    function getStagePadding() {
        const stageInstance = getStageInstance(identity);
        const stageSize = stageInstance.getSize();
        const contentWidth = stageSize.width * scale;
        const contentHeight = stageSize.height * scale;

        let left = viewPortPadding;
        let top = viewPortPadding;

        // 内容高度小于视窗高度
        if (viewHeight - top > contentHeight) {
            top = (viewHeight - contentHeight) / 2;
        }
        // 内容宽度小于视窗宽度
        if (viewWidth - left > contentWidth) {
            left = (viewWidth - contentWidth) / 2;
        }

        if (left !== paddingLeft) {
            setPaddingLeft(left);
        }
        if (top !== paddingTop) {
            setPaddingTop(top);
        }
    }

    useEffect(() => {
        getStagePadding();
    }, [scale, viewHeight, viewWidth, stageScrollPosition, stageVersion]);

    // 监听舞台宽高变化
    useEffect(() => {
        const handler = framework.events.onStageResize(() => {
            setStageVersion((prev) => prev + 1);
        });
        return () => framework.events.off(handler);
    }, []);

    return {
        paddingLeft,
        paddingTop,
    };
}
